import { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import './SessionButton.css';

function SessionButton({ 
    session, 
    isSelected, 
    onSessionClick,
    onRenameConfirm,
    assistantId,
    accessToken,
    dataTestId,
    isMobile
}) {
    const { t: tChat } = useTranslation('chat');
    
    const [isMenuActive, setIsMenuActive] = useState(false);
    const [isMobileMenu, setIsMobileMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [isEditing, setIsEditing] = useState(false);
    const [editingLabel, setEditingLabel] = useState('');
    const [displayLabel, setDisplayLabel] = useState(session.label || tChat('new_chat'));

    useEffect(() => {
        setDisplayLabel(session.label || tChat('new_chat'));
    }, [session.label, tChat]);

    const handleThreePointsClick = (e) => {
        e.stopPropagation();
        if (isMenuActive) {
            setIsMenuActive(false);
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            const mobile = isMobile ?? (window.innerWidth <= 768);
            setIsMobileMenu(mobile);
            setMenuPosition({
                top: rect.top + rect.height / 2,
                left: mobile ? rect.left - 8 : rect.right + 8
            });
            setIsMenuActive(true);
        }
    };

    const handleRenameClick = (e) => {
        e.stopPropagation();
        setIsMenuActive(false);
        setIsEditing(true);
        setEditingLabel(displayLabel);
    };

    const handleConfirmRename = async (e) => {
        if (e) e.stopPropagation();
        
        const newLabel = editingLabel.trim();
        if (!newLabel || newLabel === displayLabel) {
            setIsEditing(false);
            setEditingLabel('');
            return;
        }

        setDisplayLabel(newLabel);
        setIsEditing(false);
        setEditingLabel('');

        if (onRenameConfirm) {
            onRenameConfirm(session.session_id, newLabel);
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/update_summary_info`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    session_id: session.session_id,
                    assistant_id: assistantId,
                    label: newLabel
                })
            });

            if (!response.ok) {
                console.error('Erro ao renomear sessão');
                setDisplayLabel(session.label);
            }
        } catch (error) {
            console.error('Erro ao renomear sessão:', error);
            setDisplayLabel(session.label);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleConfirmRename();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditingLabel('');
        }
    };

    const handleOverlayClick = () => {
        setIsMenuActive(false);
    };

    if (isEditing) {
        return (
            <div className={`session-button editing ${isSelected ? 'selected' : ''}`}>
                <div className="session-rename-container">
                    <input
                        type="text"
                        className="session-rename-input"
                        value={editingLabel}
                        onChange={(e) => setEditingLabel(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                    <img 
                        src="/imagens/confirm.svg" 
                        alt="Confirm"
                        className="session-confirm-icon"
                        onClick={handleConfirmRename}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <Button 
                variant="outlined"
                disableRipple
                data-testid={dataTestId}
                className={`session-button ${isSelected ? 'selected' : ''} ${isMenuActive ? 'menu-active' : ''}`}
                onClick={() => onSessionClick(session.session_id)}
            >
                {session.status && (
                    // Etapa 4 (PBI 872): a geracao dessa conversa continuou no
                    // servidor depois do usuario sair - indicador provisorio,
                    // visual definitivo fica com o time de design.
                    <span
                        data-testid="session-status-dot"
                        className={`session-status-dot session-status-dot--${session.status}`}
                        title={session.status === 'processing' ? tChat('still_processing') : tChat('processing_done')}
                    />
                )}
                <span data-testid="session-label">{displayLabel || tChat('new_chat')}</span>
                <img
                    data-testid="three-points-icon"
                    src={isMenuActive ? "/imagens/three-points-activated.svg" : "/imagens/three-points.svg"}
                    alt="Options"
                    className={`three-points-icon ${isMenuActive ? 'active' : ''}`}
                    onClick={handleThreePointsClick}
                />
            </Button>

            {isMenuActive && (
                <>
                    <div className="session-options-overlay" onClick={handleOverlayClick} />
                    <div 
                        className="session-options-menu"
                        style={{
                            top: menuPosition.top,
                            left: menuPosition.left,
                            transform: isMobileMenu ? 'translate(-100%, -50%)' : 'translateY(-50%)'
                        }}
                        onClick={handleRenameClick}
                    >
                        <span data-testid="session-rename-option">{tChat('rename')}</span>
                    </div>
                </>
            )}
        </>
    );
}

export default SessionButton;
